import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Loader2, CheckCircle2, XCircle, Inbox, MessageSquare } from "lucide-react";
import api from "../services/api";
import { toast } from "../components/ui/sonner";

export default function AdminEditRequests() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [pendingId, setPendingId] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [actingId, setActingId] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/edit-requests?status=${status}`);
      setRows(data?.requests || []);
    } catch (e) {
      toast.error("Failed to load edit requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  const decide = async (id, decision) => {
    setActingId(id);
    try {
      await api.post(`/admin/edit-requests/${id}/decide`, {
        decision, admin_note: adminNote || undefined,
      });
      toast.success(decision === "approve" ? "Edit applied to order" : "Request rejected");
      setPendingId(null);
      setAdminNote("");
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-4 px-4 sm:px-6 py-6 max-w-5xl mx-auto" data-testid="admin-edit-requests-page">
      <div>
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-extrabold tracking-tight">Edit Requests</h1>
        <p className="text-sm text-slate-500 mt-1">
          Customer change requests on shipments where pickup has already started.
        </p>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200">
        {["pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              tab === s
                ? "border-[#FF6B00] text-[#FF6B00]"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
            data-testid={`tab-${s}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" />
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center">
          <Inbox className="h-8 w-8 mx-auto text-slate-300" />
          <p className="text-sm text-slate-500 mt-2">No {tab} requests right now.</p>
        </div>
      )}

      {!loading && rows.map((r) => (
        <div key={r._id} className="rounded-2xl border border-slate-100 bg-white p-5 hover:shadow-sm transition" data-testid={`edit-request-row-${r._id}`}>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="font-mono text-sm font-bold text-slate-900">{r.tracking_id}</span>
            <span className="text-xs text-slate-500">·</span>
            <span className="text-sm text-slate-700">{r.requested_by_name}</span>
            <span className="text-xs text-slate-400 ml-auto">{r.created_at?.replace("T", " ").slice(0, 19)}</span>
          </div>
          <div className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
            <MessageSquare className="h-4 w-4 text-slate-400" />
            "{r.reason}"
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-3 mb-1">
            {Object.entries(r.fields || {}).map(([k, v]) => (
              <li key={k} className="text-xs font-mono text-slate-600">
                <b>{k}:</b> {String(v ?? "—")}
              </li>
            ))}
          </ul>
          {r.admin_note && (
            <p className="mt-2 text-xs italic text-slate-500">Admin note: {r.admin_note}</p>
          )}
          {tab === "pending" && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              {pendingId === r._id ? (
                <div className="space-y-2" data-testid={`decide-form-${r._id}`}>
                  <textarea
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Optional note (e.g. confirmed via call)"
                    rows={2}
                    className="w-full rounded-lg border border-slate-200 p-2 text-sm focus:border-[#FF6B00] focus:outline-none"
                    data-testid={`admin-note-${r._id}`}
                  />
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={() => { setPendingId(null); setAdminNote(""); }} className="rounded-xl" size="sm">Cancel</Button>
                    <Button
                      onClick={() => decide(r._id, "reject")}
                      disabled={actingId === r._id}
                      variant="outline"
                      size="sm"
                      className="rounded-xl border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`reject-btn-${r._id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1.5" /> Reject
                    </Button>
                    <Button
                      onClick={() => decide(r._id, "approve")}
                      disabled={actingId === r._id}
                      size="sm"
                      className="rounded-xl bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`approve-btn-${r._id}`}
                    >
                      {actingId === r._id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Approve & Apply
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-end">
                  <Button onClick={() => setPendingId(r._id)} size="sm" className="rounded-xl bg-[#FF6B00] hover:bg-[#E65A00]" data-testid={`decide-btn-${r._id}`}>
                    Review & Decide
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
